/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { listVehicles } from '../../vehicles/infra/vehiclesRepo'
import { vehicleDisplay } from '../../vehicles/domain/types';
import {
  listBranchWorkOrders,
} from '../infra/workOrdersRepo';
import {
  WORK_ORDER_STATUS_LABELS,
  WORK_ORDER_STATUS_COLORS,
  ACTIVE_WORK_ORDER_STATUSES,
} from '../domain/workOrderStatus';
import { SUBJECT_LABELS } from '../domain/workOrderSubject';
import { formatOtLabel } from '../domain/otNumber';
import type { WorkOrderListItem, WorkOrderStatus } from '../domain/types';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { resolveSessionEmployee } from '../../auth/application/sessionEmployeeResolver';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';

type TabKey = 'active' | 'ready' | 'history';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'active', label: 'Activas' },
  { key: 'ready', label: 'Listas' },
  { key: 'history', label: 'Historial' },
];

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function WorkOrdersScreen() {
  const navigation = useNavigation<any>();
  const branchId = useSessionStore((s) => s.currentBranchId);
  const { canView, canViewOthers } = useBranchPermission('WorkOrders');
  const canEdit = useModulePermission('WorkOrders', 'edit');
  const canLinkSale = useModulePermission('WorkOrders', 'link_sale');
  const { entitlements } = usePlanEntitlements();
  const isOwner = entitlements?.is_owner === true;
  const canSeeAllOrders = canViewOthers || isOwner;
  const capabilityGate = useCapabilityGate('work_orders', {
    title: 'Órdenes de trabajo no disponibles',
    subtitle: 'Este rubro no tiene órdenes de trabajo.',
  });

  const [tab, setTab] = useState<TabKey>('active');
  const [orders, setOrders] = useState<WorkOrderListItem[]>([]);
  const [vehicleLabelById, setVehicleLabelById] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [noAssignedWork, setNoAssignedWork] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 400);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [search]);

  const load = useCallback(async () => {
    if (!branchId) {
      setOrders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let status: WorkOrderStatus | null = null;
      if (tab === 'ready') {
        status = 'ready';
      } else if (tab === 'history') {
        status = null;
      }

      let assignedEmployeeId: string | null = null;
      if (!canSeeAllOrders) {
        const sessionEmp = await resolveSessionEmployee();
        assignedEmployeeId = sessionEmp.employeeId;
        if (!assignedEmployeeId) {
          setOrders([]);
          setNoAssignedWork(true);
          return;
        }
      }

      const rows = await listBranchWorkOrders(
        branchId,
        status,
        debouncedSearch || null,
        assignedEmployeeId,
      );
      setNoAssignedWork(!canSeeAllOrders && rows.length === 0);
      if (tab === 'history') {
        setOrders(rows.filter((r) => !ACTIVE_WORK_ORDER_STATUSES.includes(r.status)));
      } else if (tab === 'active') {
        setOrders(rows.filter((r) => ACTIVE_WORK_ORDER_STATUSES.includes(r.status)));
      } else {
        setOrders(rows);
      }

      const orgId = await getSessionOrganizationId();
      if (orgId) {
        try {
          const vehicles = await listVehicles(orgId, {});
          const map: Record<string, string> = {};
          for (const v of vehicles) {
            map[v.id] = vehicleDisplay(v);
          }
          setVehicleLabelById(map);
        } catch {
          setVehicleLabelById({});
        }
      }
    } catch (e: any) {
      setError(e?.message ?? 'Error al cargar órdenes');
      setOrders([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [branchId, tab, debouncedSearch, canSeeAllOrders]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  useEffect(() => {
    if (branchId) void load();
  }, [debouncedSearch, load]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    void load();
  }, [load]);

  const renderItem = useCallback(
    ({ item }: { item: WorkOrderListItem }) => {
      const vehicleLabel =
        item.subjectType === 'vehicle' && item.subjectId
          ? vehicleLabelById[item.subjectId]
          : null;
      // El nombre del cliente ya está en la línea de arriba: para sujeto "cliente"
      // no repetir "Cliente: <nombre>"; para el resto, el tipo alcanza como fallback.
      const subjectLineText =
        vehicleLabel ??
        (item.subjectType !== 'customer' ? SUBJECT_LABELS[item.subjectType] : null);
      const color = WORK_ORDER_STATUS_COLORS[item.status] ?? colors.textSecondary;
      return (
        <TouchableOpacity
          style={styles.row}
          onPress={() => navigation.navigate('WorkOrderDetail', { workOrderId: item.id })}
          activeOpacity={0.6}
          testID="work-orders-list.row"
        >
          <View style={styles.rowLeft}>
            <Text style={styles.otNumber}>{formatOtLabel(item.otNumber, item.id)}</Text>
            <Text style={styles.customer} numberOfLines={1}>
              {item.customerName || 'Sin cliente'}
            </Text>
            {subjectLineText ? (
              <Text style={styles.subject} numberOfLines={1}>
                {subjectLineText}
              </Text>
            ) : null}
            <Text style={styles.date}>{formatDate(item.createdAt)}</Text>
          </View>
          <View style={[styles.badge, { backgroundColor: color }]}>
            <Text style={styles.badgeText}>
              {WORK_ORDER_STATUS_LABELS[item.status] ?? item.status}
            </Text>
          </View>
        </TouchableOpacity>
      );
    },
    [navigation, vehicleLabelById],
  );

  if (!capabilityGate.allowed) {
    return <ShellContentFrame>{capabilityGate.blocked}</ShellContentFrame>;
  }

  return (
    <ShellContentFrame>
      <View style={styles.container}>
        {!canView ? (
          <ModuleEmptyState
            title="Sin acceso"
            subtitle="No tenés permiso para ver órdenes de trabajo."
          />
        ) : (
          <>
        <View style={styles.header}>
          <Text style={styles.title}>Órdenes de trabajo</Text>
          <View style={styles.headerActions}>
            {canLinkSale || canSeeAllOrders ? (
            <TouchableOpacity
              style={styles.commissionsButton}
              onPress={() => navigation.navigate('Commissions')}
              testID="work-orders-list.commissions"
            >
              <Ionicons name="cash-outline" size={16} color={colors.accent} />
              <Text style={styles.commissionsText}>Comisiones de OTs</Text>
            </TouchableOpacity>
            ) : null}
            {canEdit ? (
            <TouchableOpacity
              style={styles.newButton}
              onPress={() => navigation.navigate('WorkOrderForm')}
              testID="work-orders-list.new"
            >
              <Text style={styles.newButtonText}>+ Nueva OT</Text>
            </TouchableOpacity>
            ) : null}
          </View>
        </View>

        <View style={styles.searchContainer}>
          <Ionicons
            name="search-outline"
            size={18}
            color={colors.textMuted}
            style={styles.searchIcon}
          />
          <TextInput
            style={styles.searchInput}
            value={search}
            onChangeText={setSearch}
            placeholder="Buscar por OT o cliente…"
            placeholderTextColor={colors.inputPlaceholder}
            autoCorrect={false}
            testID="work-orders-list.search"
          />
          {search.length > 0 ? (
            <TouchableOpacity
              onPress={() => setSearch('')}
              style={styles.searchClear}
              testID="work-orders-list.search-clear"
            >
              <Ionicons name="close-circle" size={20} color={colors.textMuted} />
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={styles.tabs}>
          {TABS.map((t) => (
            <TouchableOpacity
              key={t.key}
              style={[styles.tab, tab === t.key && styles.tabActive]}
              onPress={() => setTab(t.key)}
              testID={`work-orders-list.tab-${t.key}`}
            >
              <Text style={[styles.tabText, tab === t.key && styles.tabTextActive]}>{t.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {loading ? (
          <View style={styles.center}>
            <ActivityIndicator size="large" color={colors.accent} />
          </View>
        ) : error ? (
          <View style={styles.center}>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity style={styles.retryButton} onPress={() => void load()}>
              <Text style={styles.retryText}>Reintentar</Text>
            </TouchableOpacity>
          </View>
        ) : orders.length === 0 ? (
          <ModuleEmptyState
            title={
              search
                ? 'Sin resultados'
                : noAssignedWork
                  ? 'No tenés trabajos asignados'
                  : 'No hay órdenes de trabajo'
            }
            subtitle={
              search
                ? `No se encontraron OTs para "${search}"`
                : noAssignedWork
                  ? 'Cuando te asignen una tarea en una OT, va a aparecer acá.'
                  : 'Creá una nueva orden de trabajo para empezar.'
            }
            primaryAction={
              canEdit && !noAssignedWork
                ? {
                    label: '+ Nueva OT',
                    onPress: () => navigation.navigate('WorkOrderForm'),
                  }
                : undefined
            }
            testID="work-orders-list.empty"
          />
        ) : (
          <FlatList
            data={orders}
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
            contentContainerStyle={styles.list}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={handleRefresh}
                colors={[colors.accent]}
              />
            }
          />
        )}
          </>
        )}
      </View>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
  },
  title: { ...typography.titleLg, fontWeight: '700', color: colors.textPrimary },
  headerActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  commissionsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 8,
    backgroundColor: colors.accentSoft,
  },
  commissionsText: { color: colors.accent, fontSize: 14, fontWeight: '600' },
  newButton: {
    backgroundColor: colors.accent,
    paddingHorizontal: 14,
    paddingVertical: spacing.sm,
    borderRadius: 8,
  },
  newButtonText: { color: '#fff', fontSize: 14, fontWeight: '600' },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
  },
  searchIcon: { position: 'absolute', left: spacing.md + 8, zIndex: 1 },
  searchInput: {
    flex: 1,
    backgroundColor: colors.inputBackground,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    borderRadius: 8,
    paddingHorizontal: 36,
    paddingVertical: 9,
    fontSize: 14,
    color: colors.textPrimary,
  },
  searchClear: { position: 'absolute', right: spacing.md + 8 },
  tabs: {
    flexDirection: 'row',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.sm,
    gap: spacing.sm,
  },
  tab: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: colors.controlSurface,
  },
  tabActive: { backgroundColor: colors.accent },
  tabText: { fontSize: 14, fontWeight: '500', color: colors.textSecondary },
  tabTextActive: { color: '#fff' },
  list: { paddingHorizontal: spacing.md, paddingBottom: spacing.xl },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.surface,
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: colors.borderSoft,
  },
  rowLeft: { flex: 1, paddingRight: spacing.md },
  otNumber: { fontSize: 15, fontWeight: '700', color: colors.textPrimary },
  customer: { fontSize: 14, color: colors.textSecondary, marginTop: 2 },
  subject: { fontSize: 13, color: colors.textMuted, marginTop: 2 },
  date: { fontSize: 12, color: colors.textMuted, marginTop: 4 },
  badge: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6 },
  badgeText: { color: '#fff', fontSize: 12, fontWeight: '600' },
  separator: { height: spacing.sm },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.xl,
  },
  errorText: {
    fontSize: 15,
    color: colors.danger,
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  retryButton: {
    backgroundColor: colors.accent,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 8,
  },
  retryText: { color: '#fff', fontWeight: '600' },
});
